import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { useAppDispatch, useAppSelector } from 'lib/redux';
import getConfig from 'next/config';
import { FC, useState } from "react";
import { createSubscription, selectSubscription, updateSubscription } from './subscriptionSlice';
import { loadStripe } from '@stripe/stripe-js';
import { AlertError } from 'components';
import { Button, useToast, VStack } from '@chakra-ui/react';

const { publicRuntimeConfig: { stripeTeams, stripePublic }} = getConfig();

const stripePromise = loadStripe(stripePublic);

const style = {
  base: {
    color: '#241E24',
    fontWeight: 600,
    fontFamily: 'Quicksand, Open Sans, Segoe UI, sans-serif',
    fontSize: '16px',
    fontSmoothing: 'antialiased',

    ':focus': {
      color: '#424770',
    },

    '::placeholder': {
      color: '#9BACC8',
    },

    ':focus::placeholder': {
      color: '#CFD7DF',
    },
  },
  invalid: {
    color: '#e53e3e',
    ':focus': {
      color: '#FA755A',
    },
    '::placeholder': {
      color: '#FFCCA5',
    },
  },
};

type Props = {
  onFinish?: Function;
  label?: string;
}

export const UpdateCardForm: FC<Props> = ({ onFinish, label }) => {
  const [state, setState] = useState({
    error: null,
    isCardComplete: false,
    processing: false,
    billing: null,
  });
  const { plan: { status } } = useAppSelector(selectSubscription);

  const dispatch = useAppDispatch();
  const stripe = useStripe();
  const elements = useElements();
  const toast = useToast();

  const onSubmit = async (e) => {
    e.preventDefault();
    setState({ ...state, processing: true });

    const card = elements.getElement(CardElement);
    const createPaymentMethod = stripe.createPaymentMethod;
   
    try {
      if (status === 'active' || status === 'trialing' || status === 'past_due') {

        await dispatch(
          updateSubscription({
            card,
            createPaymentMethod
          })
        ).unwrap();
      }
      else {
        await dispatch(
          createSubscription({
            card,
            createPaymentMethod,
            plan: stripeTeams
          })
        ).unwrap();
      }

      toast({
        title: 'Card details updated',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });

    } catch (err) {
      console.error(err);
    } finally {
      setState({ ...state, processing: false });
      onFinish && onFinish();
    }
  }

  const onCardChange = ({ complete, error }: any) => {
    if (complete) {
      setState({ ...state, isCardComplete: true, error: null });
    }

    if (error) {
      setState({ ...state, error: 'Card details are invalid' });
    }
  }

  return (
    <form className="form" onSubmit={onSubmit}>
      <VStack spacing="20px" width="100%">
        {state?.error && (
          <AlertError message={state.error} style={{ marginBottom: '10px' }} />
        )}
        <CardElement options={{ style }} onChange={event => onCardChange(event)} />
        <Button
          isLoading={state?.processing}
          type="submit"
          variant="primary"
          isFullWidth={true}
          disabled={!state.isCardComplete || state.error}
        >
          {label || 'Update Card Details'}
        </Button>
      </VStack>
    </form>
  )
}

export const StripeUpdateCardForm: FC<Props> = ({ onFinish, label }) => (
  <Elements stripe={stripePromise}>
    <UpdateCardForm onFinish={onFinish} label={label} />
  </Elements>
);